import { useState, useCallback, useEffect, useOptimistic, startTransition } from 'react';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';

const useChatSessions = ({ api, onSessionChange }) => {
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);

    const [optimisticSessions, updateOptimisticSessions] = useOptimistic(
        sessions,
        (currentSessions, { sessionId, title }) =>
            currentSessions.map(session =>
                session.session_id === sessionId ? { ...session, title, updated_at: new Date().toISOString() } : session
            )
    );

    const fetchSessions = useCallback(async () => {
        setIsLoadingSessions(true);
        try {
            const response = await interactiveServiceFetch(`${api}/sessions`);

            if (!response.ok) {
                throw new Error('Failed to fetch sessions');
            }

            const data = await response.json();
            setSessions(data.sessions || []);
        } catch (err) {
            console.error('Failed to fetch sessions:', err);
        } finally {
            setIsLoadingSessions(false);
        }
    }, [api]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    const loadSession = useCallback(
        async sessionId => {
            if (!sessionId) return;

            try {
                const response = await interactiveServiceFetch(`${api}/sessions/${sessionId}`);

                if (!response.ok) {
                    throw new Error('Failed to load session');
                }

                const data = await response.json();

                // Convert session messages to chat message format
                const loadedMessages = data.messages.map((msg, index) => {
                    return {
                        id: msg.id || `${msg.role}-${sessionId}-${index}`,
                        role: msg.role,
                        content: msg.content,
                        createdAt: new Date(msg.created_at * 1000), // Convert Unix timestamp to Date
                        isThinking: false,
                        isStreaming: false,
                        steps: [],
                        isHistorical: true, // Flag to indicate this is from history
                    };
                });

                setCurrentSessionId(sessionId);
                onSessionChange?.(sessionId, false, loadedMessages);

                return loadedMessages;
            } catch (err) {
                console.error('Failed to load session:', err);
                return null;
            }
        },
        [api, onSessionChange]
    );

    const deleteSession = useCallback(
        async sessionId => {
            if (!sessionId) return false;

            try {
                const response = await interactiveServiceFetch(`${api}/sessions/${sessionId}`, {
                    method: 'DELETE',
                });

                if (!response.ok) {
                    throw new Error('Failed to delete session');
                }

                // If deleted session was current, clear it instead of creating a new one
                if (sessionId === currentSessionId) {
                    setCurrentSessionId(null);
                    onSessionChange?.(null, true);
                }

                await fetchSessions();

                return true;
            } catch (err) {
                console.error('Failed to delete session:', err);
                return false;
            }
        },
        [api, currentSessionId, onSessionChange, fetchSessions]
    );

    const updateSessionTitle = useCallback(
        async (sessionId, title) => {
            if (!sessionId || !title) return false;

            startTransition(() => {
                updateOptimisticSessions({ sessionId, title });
            });

            try {
                const response = await interactiveServiceFetch(`${api}/sessions/${sessionId}/title?title=${encodeURIComponent(title)}`, {
                    method: 'POST',
                });

                if (!response.ok) {
                    throw new Error('Failed to update session title');
                }

                setSessions(prevSessions =>
                    prevSessions.map(session =>
                        session.session_id === sessionId
                            ? { ...session, title, updated_at: new Date().toISOString() }
                            : session
                    )
                );

                return true;
            } catch (err) {
                await fetchSessions();
                return false;
            }
        },
        [api, fetchSessions, updateOptimisticSessions]
    );

    const upsertSession = useCallback((sessionId, title) => {
        setSessions(prev => {
            const exists = prev.some(session => session.session_id === sessionId);
            if (exists)
                return prev.map(session => (session.session_id === sessionId ? { ...session, title } : session));

            return [{ session_id: sessionId, title, message_count: 0 }, ...prev];
        });
    }, []);

    return {
        currentSessionId,
        setCurrentSessionId,
        sessions: optimisticSessions,
        isLoadingSessions,
        fetchSessions,
        upsertSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    };
};

export default useChatSessions;
